import React from "react";
import { Card } from "@/components/ui/card";

// ─── INR formatting helper (Indian short scale) ────────────────────────────
// 12345678 → "₹1.23 Cr", 250000 → "₹2.50 L", 12345 → "₹12.3K"
export function formatInrIndian(n) {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return "";
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  if (abs >= 1_00_00_000) return `${sign}₹${(abs / 1_00_00_000).toFixed(2)} Cr`;
  if (abs >= 1_00_000) return `${sign}₹${(abs / 1_00_000).toFixed(2)} L`;
  if (abs >= 1_000) return `${sign}₹${(abs / 1_000).toFixed(1)}K`;
  return `${sign}₹${abs.toFixed(0)}`;
}

// ─── Inline SVG sparkline — no external chart lib ──────────────────────────
export function InrSparkline({ points, width = 640, height = 60, className = "" }) {
  if (!points?.length) return null;
  const max = Math.max(1, ...points.map((p) => p.value));
  const stepX = width / Math.max(1, points.length - 1);
  const yFor = (v) => height - (v / max) * (height - 4) - 2;
  const path = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${(i * stepX).toFixed(2)},${yFor(p.value).toFixed(2)}`)
    .join(" ");
  const area = `${path} L${((points.length - 1) * stepX).toFixed(2)},${height} L0,${height} Z`;
  const peakIdx = points.reduce(
    (best, p, i, arr) => (p.value > arr[best].value ? i : best), 0);
  const peak = points[peakIdx];
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className={`w-full h-14 ${className}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="spark-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#FF7F00" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#FF7F00" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#spark-fill)" />
      <path d={path} fill="none" stroke="#FF7F00" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {peak.value > 0 && (
        <circle cx={(peakIdx * stepX).toFixed(2)} cy={yFor(peak.value).toFixed(2)} r="3" fill="#FF7F00" stroke="white" strokeWidth="1.5" />
      )}
    </svg>
  );
}

// ─── Monthly INR received card ─────────────────────────────────────────────
// `data` is the response from GET /api/dashboard/monthly-inr-received.
export function MonthlyInrCard({ data }) {
  const months = data?.months || [];
  const total = data?.fy_total_inr || 0;
  const points = months.map((m) => ({ value: m.inr_received || 0, label: m.month_label }));
  const peakIdx = points.reduce(
    (best, p, i, arr) => (p.value > arr[best].value ? i : best), 0);
  const peakLabel = points[peakIdx]?.label?.split(" ")[0];
  // Trend caption: % change vs the previous non-zero month.
  let trend = null;
  const nonZeroIdxs = months.map((m, i) => (m.inr_received > 0 ? i : -1)).filter((i) => i >= 0);
  if (nonZeroIdxs.length >= 2) {
    const last = nonZeroIdxs[nonZeroIdxs.length - 1];
    const prev = nonZeroIdxs[nonZeroIdxs.length - 2];
    const a = months[prev].inr_received;
    const b = months[last].inr_received;
    if (a > 0) {
      const pct = ((b - a) / a) * 100;
      trend = { pct, up: pct >= 0, peak: peakLabel };
    }
  }
  return (
    <Card className="border-slate-200 shadow-none p-6" data-testid="monthly-inr-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-heading font-semibold text-slate-900">Monthly INR received</h2>
          <p className="text-xs text-slate-500 mt-0.5">FY {data.financial_year} · realized bank credits, in Indian rupees.</p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wider text-slate-500">FY total</div>
          <div className="text-2xl font-heading font-semibold text-slate-900" data-testid="monthly-inr-total">
            {formatInrIndian(total)}
          </div>
        </div>
      </div>
      <div className="mt-4">
        <InrSparkline points={points} />
        {trend && (
          <div className="mt-1 text-[11px] text-slate-500 flex items-center gap-1" data-testid="monthly-inr-trend">
            <span className={trend.up ? "text-emerald-700 font-medium" : "text-amber-700 font-medium"}>
              {trend.up ? "▲" : "▼"} {Math.abs(trend.pct).toFixed(1)}%
            </span>
            <span>vs previous month · peak {trend.peak}</span>
          </div>
        )}
      </div>
      <table className="w-full mt-4 text-sm">
        <thead>
          <tr className="text-slate-500 text-xs uppercase tracking-wider">
            <th className="text-left py-2">Month</th>
            <th className="text-right py-2">INR received</th>
            <th className="text-right py-2">Payments</th>
            <th className="text-right py-2">Pending conversion</th>
          </tr>
        </thead>
        <tbody>
          {months.map((m) => (
            <tr key={m.month} className="border-t border-slate-100" data-testid={`monthly-row-${m.month}`}>
              <td className="py-2 text-slate-700">{m.month_label}</td>
              <td className="py-2 text-right font-medium text-slate-900">
                {m.inr_received > 0 ? formatInrIndian(m.inr_received) : <span className="text-slate-300">—</span>}
              </td>
              <td className="py-2 text-right text-slate-600">{m.payment_count || "—"}</td>
              <td className={`py-2 text-right ${m.pending_count > 0 ? "text-amber-700 font-medium" : "text-slate-400"}`}>
                {m.pending_count || "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}
